import React, { FC, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import {
  ThemeProvider,
  Grid,
  LinearProgress,
  styled,
} from "@mui/material";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "./telemetry/appInsights";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "./authConfig";

import IFrame from "./components/IFrame/IFrame";
import UserPage from "./components/UserPage/UserPage";
import IOPDrawer from "./components/IOPDrawer/IOPDrawer";
import PowerBI from "./components/PowerBI/PowerBI";
import ContentPage from "./components/ContentPage/ContentPage";
import theme from "./theme";
import { fetchUserAction } from "./redux/actions";

// Redux connection
const mapState = (state: State) => ({
  user: state.user,
});
const mapDispatchToProps = { fetchUser: fetchUserAction };
const connector = connect(mapState, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

// Styled components
const RootContainer = styled("div")({
  display: "flex",
  minHeight: "100vh",
});

const ContentContainer = styled("main")({
  backgroundColor: "#f4f4f4",
  display: "flex",
  flexGrow: 1,
});

const UpdateTitle: FC = () => {
  const location = useLocation();

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const routeName = pathParts[pathParts.length - 1] || "Home";

    const safeRouteName = routeName
      .normalize("NFKC")
      .replace(/[^a-zA-Z0-9-_ ]/g, "")
      .trim()
      .slice(0, 50);

    document.title = `OI - ${safeRouteName.charAt(0).toUpperCase() + safeRouteName.slice(1)}`;
  }, [location.pathname]);

  return null;
};

const App: FC<Props> = ({ user, fetchUser }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Handle redirect response and set active account
  useEffect(() => {
    instance.handleRedirectPromise().then((response) => {
      if (response) {
        instance.setActiveAccount(response.account);
      } else {
        const activeAccount = instance.getActiveAccount();
        if (!activeAccount && accounts.length > 0) {
          instance.setActiveAccount(accounts[0]);
        }
      }
    });
  }, [instance, accounts]);

  // Silent SSO attempt
  useEffect(() => {
    const attemptSilentSSO = async () => {
      try {
        const activeAccount = instance.getActiveAccount();
        if (activeAccount) {
          await instance.ssoSilent(loginRequest);
        }
      } catch (error) {
        console.warn("Silent SSO failed", error);
      }
    };
    attemptSilentSSO();
  }, [instance]);

  // Fetch user after authentication
  useEffect(() => {
    const fetchUserSecurely = async () => {
      try {
        const activeAccount = instance.getActiveAccount();
        if (activeAccount) {
          await instance.acquireTokenSilent({
            ...loginRequest,
            account: activeAccount,
          });
          fetchUser(); 
        }
      } catch (error) {
        console.error("Token acquisition failed", error);
        instance.loginRedirect(loginRequest);
      }
    };

    if (isAuthenticated) {
      fetchUserSecurely();
    }
  }, [isAuthenticated, fetchUser, instance]);

  // Auto-login if not authenticated and MSAL is idle
  useEffect(() => {
    if (!isAuthenticated && inProgress === "none") {
      instance.loginRedirect(loginRequest);
    }
  }, [isAuthenticated, inProgress, instance]);

  return (
    <Router>
      <ThemeProvider theme={theme}>
        {inProgress !== "none" ? (
          <Grid container justifyContent="center" alignItems="center" style={{ minHeight: "100vh" }}>
            <Grid item xs={6}>
              <LinearProgress color="secondary" />
            </Grid>
          </Grid>
        ) : user ? (
          <RootContainer style={{ overflow: "hidden" }}>
            <IOPDrawer />
            <ContentContainer>
              <UpdateTitle />
              <Routes>
                <Route path="/" element={<ContentPage />} />
                <Route path="/powerbi/:path" element={<PowerBI type="powerbi" />} />
                <Route path="/embed/:path" element={<IFrame type="embed" />} />
                <Route path="/instructions/:path" element={<IFrame type="instructions" />} />
                <Route path="/page/:path" element={<ContentPage />} />
                <Route path="/user" element={<UserPage />} />
              </Routes>
            </ContentContainer>
          </RootContainer>
        ) : (
          <Grid container justifyContent="center" alignItems="center" style={{ minHeight: "100vh" }}>
            <Grid item xs={6}>
              <LinearProgress color="secondary" />
            </Grid>
          </Grid>
        )}
      </ThemeProvider>
    </Router>
  );
};

export default withAITracking(reactPlugin, connector(App));